// ─────────────────────────────────────────────────────────────────────────────
// DataJadwal.jsx
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import axios from "axios";
import { FiPlus, FiEdit2, FiTrash2 } from "react-icons/fi";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import AdminLayout from "../../components/layout/AdminLayout";
import { useAuth } from "../../context/AuthContext"; 
import FormJadwal from "./FormJadwal";
import ConfirmDeleteModal from "../../components/modal/Delete";

const HARI_ORDER = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];

// Menggunakan 'export default' agar sesuai dengan yang dicari oleh AppRoutes.jsx
export default function DataJadwal() {
  const { token } = useAuth(); 
  const [jadwal, setJadwal] = useState([]);
  const [kelasList, setKelasList] = useState([]);
  const [selectedKelas, setSelectedKelas] = useState("");
  const [search, setSearch] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const formatJam = (jam) => (!jam ? "" : jam.slice(0, 5).replace(":", "."));

  const fetchKelas = async () => {
    if (!token) return; 
    try {
      const res = await axios.get("https://backend.smpn1kamal.sch.id/api/kelas", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setKelasList(res.data);
      const kelas7A = res.data.find((k) => k.nama_kelas === "7A");
      if (kelas7A) setSelectedKelas(kelas7A.id_kelas.toString());
      else if (res.data.length > 0) setSelectedKelas(res.data[0].id_kelas.toString());
    } catch { 
      toast.error("Gagal mengambil data kelas"); 
    }
  };

  const fetchJadwal = async () => {
    if (!token) return; 
    try {
      const res = await axios.get("https://backend.smpn1kamal.sch.id/api/jadwal", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setJadwal(res.data);
    } catch { 
      toast.error("Gagal mengambil data jadwal"); 
    }
  };

  useEffect(() => { 
    if (token) {
      fetchKelas(); 
      fetchJadwal(); 
    }
  }, [token]);

  const openConfirmDelete = (item) => { 
    setItemToDelete(item); 
    setIsDeleteModalOpen(true); 
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      await axios.delete(`https://backend.smpn1kamal.sch.id/api/jadwal/${itemToDelete.id_jadwal}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Jadwal berhasil dihapus");
      fetchJadwal();
      setIsDeleteModalOpen(false);
    } catch { 
      toast.error("Gagal menghapus jadwal"); 
    } finally { 
      setIsDeleting(false); 
      setItemToDelete(null); 
    }
  };

  const filteredJadwal = selectedKelas
    ? jadwal.filter((j) => {
        const matchKelas = j.id_kelas === parseInt(selectedKelas);
        const keyword = search.toLowerCase();
        return matchKelas && (
          j.nama_mapel?.toLowerCase().includes(keyword) ||
          j.nama_lengkap?.toLowerCase().includes(keyword)
        );
      })
    : [];

  const sortedJadwal = [...filteredJadwal].sort((a, b) => {
    const di = HARI_ORDER.indexOf(a.hari) - HARI_ORDER.indexOf(b.hari);
    return di !== 0 ? di : a.jam_mulai.localeCompare(b.jam_mulai);
  });

  const groupedByHari = HARI_ORDER.reduce((acc, hari) => {
    const items = sortedJadwal.filter((j) => j.hari?.toLowerCase() === hari.toLowerCase());
    if (items.length > 0) acc[hari] = items;
    return acc;
  }, {});

  const handleDownloadPDF = () => {
    if (!selectedKelas) { toast.error("Pilih kelas terlebih dahulu"); return; }
    const kelasNama = kelasList.find((k) => k.id_kelas === parseInt(selectedKelas))?.nama_kelas || "";
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 20;

    doc.addImage("/logo.png", "PNG", margin, 12, 18, 18);
    doc.setFont("times", "bold"); doc.setFontSize(16);
    doc.text("UPTD SMPN 1 KAMAL", pageW / 2, 16, { align: "center" });
    doc.setFont("times", "normal"); doc.setFontSize(11);
    doc.text("Jadwal Pelajaran", pageW / 2, 22, { align: "center" });
    doc.setFont("times", "bold"); doc.setFontSize(13);
    doc.text(`Kelas ${kelasNama}`, pageW / 2, 28, { align: "center" });
    
    const now = new Date();
    const y = now.getFullYear();
    const tahunAjaran = now.getMonth() >= 6 ? `${y}/${y+1}` : `${y-1}/${y}`;
    doc.setFont("times", "normal"); doc.setFontSize(10);
    doc.text(`Tahun Pelajaran ${tahunAjaran}`, pageW / 2, 34, { align: "center" });
    doc.setDrawColor(37, 99, 235); doc.setLineWidth(0.7);
    doc.line(margin, 38, pageW - margin, 38);

    const rows = [];
    HARI_ORDER.forEach((hari) => {
      const dataHari = sortedJadwal.filter((j) => j.hari?.toLowerCase() === hari.toLowerCase());
      if (!dataHari.length) return;
      dataHari.forEach((item, idx) => rows.push({
        isFirst: idx === 0, hariLabel: hari,
        mapel: item.nama_mapel, guru: item.nama_lengkap,
        jamMulai: formatJam(item.jam_mulai), jamSelesai: formatJam(item.jam_selesai),
      }));
    });

    autoTable(doc, {
      startY: 42, margin: { left: margin, right: margin },
      head: [["Hari", "Waktu", "Mata Pelajaran", "Guru"]],
      body: rows.map((r) => [r.isFirst ? r.hariLabel : "", `${r.jamMulai} – ${r.jamSelesai}`, r.mapel, r.guru]),
      styles: { font: "times", fontSize: 10, cellPadding: 4, textColor: [0, 0, 0], lineWidth: 0 },
      headStyles: { fillColor: [37, 99, 235], textColor: [255, 255, 255], fontStyle: "bold", halign: "center" },
      alternateRowStyles: { fillColor: [240, 244, 255] },
      columnStyles: { 0: { cellWidth: 25, fontStyle: "bold" }, 1: { cellWidth: 35, halign: "center" }, 2: { cellWidth: 65 }, 3: { cellWidth: 45 } },
      didDrawCell(data) {
        if (data.section !== "body") return;
        const rowIdx = data.row.index;
        if (rowIdx === 0) return;
        if (rows[rowIdx]?.isFirst) {
          doc.setDrawColor(37, 99, 235); doc.setLineWidth(0.4);
          doc.line(margin, data.cell.y, pageW - margin, data.cell.y);
        }
      },
    });

    const footerY = pageH - 10;
    doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.3);
    doc.line(margin, footerY - 4, pageW - margin, footerY - 4);
    doc.setFont("times", "normal"); doc.setFontSize(9);
    doc.text(`Dicetak: ${now.toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" })}`, margin, footerY);
    doc.text("SIPENSA — UPTD SMPN 1 Kamal", pageW - margin, footerY, { align: "right" });
    doc.save(`Jadwal_Kelas_${kelasNama}.pdf`);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="space-y-3">
          <div className="hidden md:flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-indigo-600">Data Jadwal Pembelajaran</h1>
              <p className="text-sm text-gray-500">SMPN 1 Kamal</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handleDownloadPDF} className="flex items-center gap-2 bg-green-500 text-white px-5 py-2 rounded-full text-sm hover:bg-green-600 transition">↓ Unduh PDF</button>
              <button onClick={() => { setEditData(null); setOpenForm(true); }} className="flex items-center gap-2 bg-indigo-500 text-white px-5 py-2 rounded-full text-sm hover:bg-indigo-600 transition"><FiPlus /> Tambah</button>
            </div>
          </div>
          <div className="md:hidden space-y-3">
            <div className="flex justify-between items-center"> 
              <div><h1 className="text-2xl font-bold text-indigo-600">Data Jadwal</h1><p className="text-sm text-gray-500">SMPN 1 Kamal</p></div>
              <div className="flex gap-2">
                <button onClick={handleDownloadPDF} className="bg-green-500 text-white px-3 py-2 rounded-full text-sm hover:bg-green-600 transition">↓ PDF</button>
                <button onClick={() => { setEditData(null); setOpenForm(true); }} className="flex items-center gap-1.5 bg-indigo-500 text-white px-3 py-2 rounded-full text-sm hover:bg-indigo-600 transition"><FiPlus /> Tambah</button>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-md p-5">
          <div className="flex flex-row items-center justify-between gap-3 mb-5">
            <select value={selectedKelas} onChange={(e) => setSelectedKelas(e.target.value)} className="border border-slate-300 rounded-full px-3 py-2 text-xs md:text-sm">
              {kelasList.map((k) => (<option key={k.id_kelas} value={k.id_kelas}>{k.nama_kelas}</option>))}
            </select>
            <input type="text" placeholder="Cari..." value={search} onChange={(e) => setSearch(e.target.value)} className="border border-slate-300 rounded-full px-3 py-2 text-xs md:text-sm w-40 md:w-72" />
          </div>

          {/* Desktop */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm" style={{ tableLayout: "fixed" }}>
              <thead className="bg-gray-100 text-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left w-[13%]">HARI</th>
                  <th className="px-4 py-3 text-left w-[18%]">WAKTU</th>
                  <th className="px-4 py-3 text-left w-[28%]">MATA PELAJARAN</th>
                  <th className="px-4 py-3 text-left w-[25%]">GURU</th>
                  <th className="px-4 py-3 text-center w-[16%]">AKSI</th>
                </tr>
              </thead>
              <tbody>
                {sortedJadwal.length === 0 ? (
                  <tr><td colSpan={5} className="text-center text-gray-400 py-10">{search ? `Pencarian "${search}" tidak ditemukan.` : "Jadwal belum tersedia."}</td></tr>
                ) : (
                  sortedJadwal.map((item, i) => {
                    const isNewDay = i === 0 || item.hari !== sortedJadwal[i-1].hari;
                    return (
                      <tr key={item.id_jadwal} className={`hover:bg-gray-50 transition ${isNewDay && i !== 0 ? "border-t-2 border-gray-200" : "border-t border-gray-100"}`}>
                        <td className={`px-4 py-3 font-semibold text-gray-800 ${!isNewDay ? "text-transparent select-none" : ""}`}>{item.hari}</td>
                        <td className="px-4 py-3 text-gray-600 text-xs tabular-nums font-medium">{formatJam(item.jam_mulai)} – {formatJam(item.jam_selesai)}</td>
                        <td className="px-4 py-3"><span className="inline-block bg-indigo-50 text-indigo-600 text-xs px-3 py-1 rounded-full font-semibold">{item.nama_mapel}</span></td>
                        <td className="px-4 py-3 text-gray-700 font-medium">{item.nama_lengkap}</td>
                        <td className="px-4 py-3">
                          <div className="flex justify-center gap-2">
                            <button onClick={() => { setEditData(item); setOpenForm(true); }} className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition"><FiEdit2 size={13} /></button>
                            <button onClick={() => openConfirmDelete(item)} className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-100 transition"><FiTrash2 size={13} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile */}
          <div className="flex flex-col gap-4 md:hidden">
            {sortedJadwal.length === 0 ? (
              <p className="text-center text-gray-400 py-10">{search ? `Pencarian "${search}" tidak ditemukan.` : "Jadwal belum tersedia."}</p>
            ) : (
              Object.entries(groupedByHari).map(([hari, items]) => (
                <div key={hari}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">{hari}</span>
                    <div className="flex-1 h-px bg-indigo-100" />
                  </div>
                  <div className="flex flex-col gap-2">
                    {items.map((item) => (
                      <div key={item.id_jadwal} className="border rounded-xl p-4 shadow-sm">
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <span className="inline-block bg-indigo-50 text-indigo-600 text-xs px-3 py-1 rounded-full font-semibold mb-1">{item.nama_mapel}</span>
                            <p className="text-sm text-gray-700 truncate">{item.nama_lengkap}</p>
                            <p className="text-xs text-gray-400 tabular-nums mt-0.5">{formatJam(item.jam_mulai)} – {formatJam(item.jam_selesai)}</p>
                          </div>
                          <div className="flex gap-1.5 shrink-0">
                            <button onClick={() => { setEditData(item); setOpenForm(true); }} className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition"><FiEdit2 size={13} /></button>
                            <button onClick={() => openConfirmDelete(item)} className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-100 transition"><FiTrash2 size={13} /></button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Modal-modal diletakkan aman di paling bawah tanpa *conditional rendering* `{openForm && ...}` */}
        <FormJadwal 
          isOpen={openForm} 
          onClose={() => { setOpenForm(false); fetchJadwal(); }} 
          editData={editData} 
        />
        
        <ConfirmDeleteModal 
          open={isDeleteModalOpen} 
          onClose={() => setIsDeleteModalOpen(false)} 
          onConfirm={handleConfirmDelete} 
          loading={isDeleting} 
          title="Hapus Jadwal" 
          description={itemToDelete ? `Apakah Anda yakin ingin menghapus jadwal ${itemToDelete.nama_mapel} hari ${itemToDelete.hari}?` : ""} 
        />
      </div>
    </AdminLayout>
  );
}